import { applyFilters, doRequest } from "./helpers";

const tagPaths = {
  Case: "/case/tags",
  CampaignTargetCall: "/call/tags",
  CampaignTargetEmail: "/email/tags",
  Contact: "/contact/tags", // acl
  Entity: "/entity/tags", // acl
  EmailFooter: "/email/assets/footers/tags", //acl
  EmailSignature: "/email/assets/signatures/tags", // acl
  EmailUrl: "/email_url/tags", //acl
  EmailTemplate: "/email/assets/templates/tags",
  DataJob: "/data_job/tags",
  Lead: "/lead/tags", // acl
  RevenueOpportunity: "/revenue_opportunity/tags",
  Machine: "/machine/tags",
};

const CUSTOM_TABLE_PREFIX = "CustomTable:";

const isCustomTableModel = modelType =>
  typeof modelType === "string" && modelType.startsWith(CUSTOM_TABLE_PREFIX);

// Custom-table rows can't use the static `tagPaths` map: each table has its
// own slug in the URL. `modelType` is "CustomTable:<slug>".
function customTableRowsTagsPath(modelType) {
  const slug = modelType.slice(CUSTOM_TABLE_PREFIX.length);
  return `/custom_tables/${slug}/rows/tags`;
}

function customTableRowTagPath(modelType, rowId) {
  const slug = modelType.slice(CUSTOM_TABLE_PREFIX.length);
  return `/custom_tables/${slug}/rows/${rowId}/tags`;
}

export default class TagService {
  static async getTags(params = null) {
    let path = `/tag/`;
    path = applyFilters(params, path);
    return doRequest("GET", path);
  }

  // The tags getTags would list, without roles or metadata fields; enough for
  // a table's tag filter and much cheaper to build for large tag sets.
  static async getTagOptions({ model, forFiltering = true }) {
    const path = applyFilters(
      { model, for_filtering: forFiltering, options: true },
      "/tag/"
    );
    return doRequest("GET", path);
  }

  static async getTagsPage({
    model,
    page,
    perPage,
    search,
    includeDisabled,
    dialSessionId,
    ids,
  }) {
    const path = applyFilters(
      {
        model,
        for_filtering: false,
        ids: ids?.join(","),
        page,
        per_page: perPage,
        search: search || undefined,
        include_disabled: includeDisabled || undefined,
        dial_session_id: dialSessionId,
      },
      "/tag/"
    );
    return doRequest("GET", path);
  }

  static async getTagWithMetadata({ model, tagId }) {
    const path = applyFilters(
      { model, for_filtering: false, ids: tagId },
      "/tag/"
    );
    return doRequest("GET", path);
  }

  static async getTag(id) {
    return doRequest("GET", `/tag/${id}`);
  }

  static async getTagsTable(filters = null) {
    let path = "/tag/table/";
    path = applyFilters(filters, path);
    return doRequest("GET", path);
  }

  static async createTag(data) {
    let path = `/tag/`;
    return doRequest("POST", path, { data });
  }

  static async editTag(id, data) {
    let path = `/tag/${id}`;
    return doRequest("PUT", path, { data });
  }

  static async deleteTag(id) {
    let path = `/tag/${id}`;
    return doRequest("DELETE", path);
  }

  static async bulkDeleteTags(tags_ids) {
    let path = `/tag/bulk_delete`;
    return doRequest("DELETE", path, {
      data: {
        tags_ids,
      },
    });
  }

  static async removeTag(tag, className, itemIds) {
    if (isCustomTableModel(className)) {
      return doRequest("DELETE", customTableRowsTagsPath(className), {
        data: { tag, item_ids: itemIds },
      });
    }
    let path = tagPaths[className];
    return doRequest("DELETE", path, {
      data: {
        tag: tag,
        item_ids: itemIds,
      },
    });
  }

  static async removeTags(tags, className, itemIds) {
    const path = isCustomTableModel(className)
      ? customTableRowsTagsPath(className)
      : tagPaths[className];
    return doRequest("DELETE", path, { data: { tags, item_ids: itemIds } });
  }

  static async addTag(tag, className, itemIds, metadata) {
    if (isCustomTableModel(className)) {
      return doRequest("POST", customTableRowsTagsPath(className), {
        data: { tag, item_ids: itemIds, metadata },
      });
    }
    let path = tagPaths[className];
    return doRequest("POST", path, {
      data: {
        tag: tag,
        item_ids: itemIds,
        metadata,
      },
    });
  }

  static async updateModelTag(tag, modelType, modelId, metadata) {
    if (isCustomTableModel(modelType)) {
      return doRequest("PUT", customTableRowTagPath(modelType, modelId), {
        data: { tag, metadata },
      });
    }
    const path = tagPaths[modelType];
    return doRequest("PUT", path, {
      data: {
        tag,
        metadata,
        item_id: modelId,
      },
    });
  }

  static async bulkUpdateCategory(categoryId, tagIds) {
    return doRequest("PUT", "/tag/update_category", {
      data: {
        category_id: categoryId,
        tag_ids: tagIds,
      },
    });
  }
}
